import {
  CharityTypeEnum,
  CharityVettingUrlType,
} from 'features/charity-vetting/types/charityVettingTypes';
import { z } from 'zod';

import ukMsg from './messages';

import type {
  PageSchemaFactory,
  PageSchemaMap,
} from 'features/charity-vetting/common-flow/validation/pageSchemaRegistry';

/**
 * Per-page Zod schemas for the UK vetting flow. Each validates only the field(s)
 * its page owns; sibling fields (read for conditionals) are already-set form
 * state and pass through. Cross-field rules use `superRefine` so the sibling is
 * visible.
 */

const wrap = (charityOnboarding: z.ZodTypeAny) => z.looseObject({ charityOnboarding });

const isFoodFlow = (donationPreferences: unknown) =>
  donationPreferences === 'ONLY_FOOD' || donationPreferences === 'FOOD_AND_NON_FOOD';

// registration page — charityType must be selected
export const ukRegistrationPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ charityType: z.nativeEnum(CharityTypeEnum) }));

// charity-number page — required only when charityType is REGISTERED_CHARITY
export const ukCharityNumberPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        charityType: z.nativeEnum(CharityTypeEnum).nullable().optional(),
        charityRegistrationId: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (
          data.charityType === CharityTypeEnum.REGISTERED_CHARITY &&
          !data.charityRegistrationId?.trim()
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(ukMsg.charityVettingRegistrationIdRequiredUK),
            path: ['charityRegistrationId'],
          });
        }
      }),
  );

// company-registration page — isCompany must be answered
export const ukCompanyRegistrationPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ isCompany: z.boolean() }));

// company-number page — required only when isCompany is true
export const ukCompanyNumberPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        isCompany: z.boolean().optional(),
        companyRegistrationNumber: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data.isCompany === true && !data.companyRegistrationNumber?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(ukMsg.charityVettingCompanyNumberRequiredUK),
            path: ['companyRegistrationNumber'],
          });
        }
      }),
  );

// pli page — hasPli must be answered
export const ukPliPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ hasPli: z.boolean() }));

// pli-cover page — pliCover.value required when hasPli is true
export const ukPliCoverPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z
      .looseObject({
        hasPli: z.boolean().optional(),
        pliCover: z.looseObject({ value: z.number().nullable().optional() }).optional(),
      })
      .superRefine((data, ctx) => {
        if (data.hasPli === true && !data.pliCover?.value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(ukMsg.charityVettingPliRequiredUK),
            path: ['pliCover', 'value'],
          });
        }
      }),
  );

// food-hygiene page — isFsaRated required only in food flows
export const ukFoodHygienePageSchema: PageSchemaFactory = () =>
  wrap(
    z
      .looseObject({
        donationPreferences: z.string().optional(),
        isFsaRated: z.boolean().nullable().optional(),
      })
      .superRefine((data, ctx) => {
        if (isFoodFlow(data.donationPreferences) && typeof data.isFsaRated !== 'boolean') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'isFsaRated-required',
            path: ['isFsaRated'],
          });
        }
      }),
  );

// food-hygiene-certificate page — hasHygieneCertificate required only in food flows
export const ukFoodHygieneCertificatePageSchema: PageSchemaFactory = () =>
  wrap(
    z
      .looseObject({
        donationPreferences: z.string().optional(),
        hasHygieneCertificate: z.boolean().nullable().optional(),
      })
      .superRefine((data, ctx) => {
        if (
          isFoodFlow(data.donationPreferences) &&
          typeof data.hasHygieneCertificate !== 'boolean'
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'hasHygieneCertificate-required',
            path: ['hasHygieneCertificate'],
          });
        }
      }),
  );

export const ukPageSchemas: PageSchemaMap = {
  [CharityVettingUrlType.REGISTRATION_DETAILS]: ukRegistrationPageSchema,
  [CharityVettingUrlType.CHARITY_NUMBER]: ukCharityNumberPageSchema,
  [CharityVettingUrlType.COMPANY_REGISTRATION]: ukCompanyRegistrationPageSchema,
  [CharityVettingUrlType.COMPANY_NUMBER]: ukCompanyNumberPageSchema,
  [CharityVettingUrlType.PLI]: ukPliPageSchema,
  [CharityVettingUrlType.PLI_COVER]: ukPliCoverPageSchema,
  [CharityVettingUrlType.FOOD_HYGIENE]: ukFoodHygienePageSchema,
  [CharityVettingUrlType.FOOD_HYGIENE_CERTIFICATE]: ukFoodHygieneCertificatePageSchema,
};
